export type LockPurpose = 'restore' | 'resync';

/**
 * Used to acquire a cross-tab lock using the weblock API.
 * It ensures
 * https://developer.mozilla.org/en-US/docs/Web/API/Web_Locks_API
 */
export class SynchronisationLock {
	private dbName: string | undefined;
	private _hasLock: boolean = false;
	private _lockPurpose: LockPurpose | undefined;

	public get hasLock() {
		return this._hasLock;
	}

	public get lockPurpose() {
		return this._lockPurpose;
	}

	init(dbName: string) {
		this.dbName = dbName;
	}

	/**
	 * Release the lock so it can be used elsewhere
	 */
	public releaseLock(): undefined {
		this._releaseLock();
	}
	private _releaseLock = () => {};

	private holdLock = async () => {
		this._hasLock = true;
		let resolve: (value: unknown) => void;
		this._releaseLock = () => {
			// resolving the promise will release the lock
			resolve({});
			this._hasLock = false;
			this._lockPurpose = undefined;
		};
		return new Promise((res) => {
			resolve = res;
		});
	};

	/**
	 * Acquire the lock for the page
	 *
	 * **Waiting on this function will block until the lock is acquired then released, or the lock request is aborted.**
	 * If waitUntilAvailable is set to `true`, the lock request will wait for the lock to become available.
	 * If waitUntilAvailable is set to `false`, the lock request will abort if the lock is not available at the time it's requested.
	 *
	 * @param callback - The callback to run when the lock is acquired or the lock request is aborted
	 * @param waitUntilAvailable - When set to true the lock request will wait until the lock is available
	 */
	public async acquireLock(
		callback?: (granted: boolean) => Promise<void>,
		waitUntilAvailable = false,
		purpose?: LockPurpose,
	) {
		if (!this.dbName) {
			return Promise.reject(new Error('SynchronisationLock not initialized.'));
		}

		await navigator.locks?.request(
			this.dbName,
			{ ifAvailable: !waitUntilAvailable },
			async (lock) => {
				if (lock === null) {
					await callback?.(false);
					return;
				}

				const promise = this.holdLock();
				this._lockPurpose = purpose;

				await callback?.(true);
				return promise;
			},
		);
	}

	/**
	 * If the lock is currently taken and therefore we shouldn't send any data
	 */
	public async isLocked() {
		if (!this.dbName) {
			return false;
		}
		const locks = await navigator.locks?.query();
		const relevantLocks = locks?.held ? locks.held.filter((lock) => lock.name === this.dbName) : [];
		if (relevantLocks && relevantLocks.length > 0) {
			return true;
		} else {
			return false;
		}
	}
}
